import React, { useCallback, useState } from 'react';
import { memo } from 'react';
import BaseNode from '../BaseNode';
import { Box, Typography, Chip, alpha, useTheme, Tabs, Tab } from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import { DynamicControls } from '../../core/NodeControls';
import { getLayerParameters } from '../../utils/tensorflowParameters';

/**
 * DenseNode represents a fully connected layer in the neural network
 */
const DenseNode = ({ data, id, selected, isConnectable }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  
  // Get the TensorFlow parameters for the Dense layer
  const denseParams = getLayerParameters('Dense');
  
  // Handler to update node data
  const handleDataChange = useCallback((key, value) => {
    if (data.onChange) {
      data.onChange(id, key, value);
    }
  }, [data, id]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Generate preview content
  const renderPreview = () => {
    return (
      <>
        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, textAlign: 'center' }}>
          Units: {data.units || 64}
        </Typography>
        
        <Chip 
          label={data.activation || 'relu'} 
          size="small"
          sx={{ 
            fontSize: '0.7rem',
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
          }}
        />
        
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            mt: 1,
          }}
        >
          {Array.from({ length: Math.min(5, Math.ceil((data.units || 64) / 10)) }).map((_, i) => (
            <Box
              key={i}
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '2px',
                backgroundColor: theme.palette.primary.main,
                margin: '2px',
              }}
            />
          ))}
          {(data.units || 64) > 50 && (
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
              +{Math.ceil(((data.units || 64) - 50) / 10)} more
            </Typography>
          )}
        </Box>
      </>
    );
  };
  
  // Define which parameters should appear in the basic tab
  const basicParams = ['units', 'activation', 'use_bias'];
  
  // Controls for the node configuration popover with tabs for basic and advanced options
  const renderControls = () => {
    return (
      <>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          variant="fullWidth"
        >
          <Tab label="Basic" />
          <Tab label="Advanced" />
        </Tabs>
        
        {/* Basic parameters tab */}
        {tabValue === 0 && (
          <DynamicControls
            parameters={denseParams}
            values={data}
            onChange={handleDataChange}
            filter={(key) => basicParams.includes(key)}
          />
        )}
        
        {/* Advanced parameters tab */}
        {tabValue === 1 && (
          <DynamicControls
            parameters={denseParams}
            values={data}
            onChange={handleDataChange}
            filter={(key) => !basicParams.includes(key)}
          />
        )}
      </>
    );
  };
  
  return (
    <BaseNode
      data={data}
      id={id}
      selected={selected}
      isConnectable={isConnectable}
      icon={<GridOnIcon fontSize="small" />}
      nodeType="core"
      preview={renderPreview()}
      controls={renderControls()}
      onDelete={data.onDelete}
    />
  );
};

export default memo(DenseNode); 